import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { DONATE_URL } from "@/lib/links";
import { motion } from "framer-motion";
import { FileText, HeartHandshake, ArrowRight, ClipboardList, Camera, Loader2, UserCircle2, FolderOpen, Upload, Download, Trash2, CalendarCheck, MapPin, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MyChapter } from "@/components/MyChapter";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
  };
  return <span className={`inline-block px-2.5 py-1 text-xs uppercase tracking-wider font-semibold ${map[status] || "bg-slate-100"}`}>{status}</span>;
};

export default function MemberAccount() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docRef = useRef(null);
  const [myEvents, setMyEvents] = useState([]);

  const loadRsvps = () => api.get("/me/rsvps").then(({ data }) => setMyEvents(data)).catch(() => {});

  const cancelRsvp = async (eventId) => {
    try {
      await api.delete(`/me/rsvps/${eventId}`);
      setMyEvents((list) => list.filter((e) => e.id !== eventId));
      toast.success("RSVP canceled");
    } catch { toast.error("Could not cancel"); }
  };

  const loadAvatar = () => {
    api.get("/me/avatar", { responseType: "blob" })
      .then((r) => setAvatarUrl(URL.createObjectURL(r.data)))
      .catch(() => setAvatarUrl(null));
  };

  const loadDocs = () => {
    api.get("/me/documents").then(({ data }) => setDocs(data)).catch(() => {});
  };

  useEffect(() => {
    Promise.all([api.get("/me/applications"), api.get("/me/donations")])
      .then(([a, d]) => { setApps(a.data); setDonations(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadAvatar();
    loadDocs();
    loadRsvps();
  }, []);

  const uploadDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/me/documents", fd);
      toast.success("Document uploaded");
      loadDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingDoc(false);
      if (docRef.current) docRef.current.value = "";
    }
  };

  const downloadDoc = async (id) => {
    try {
      const r = await api.get(`/me/documents/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open document"); }
  };

  const deleteDoc = async (id) => {
    try { await api.delete(`/me/documents/${id}`); toast.success("Removed"); loadDocs(); }
    catch { toast.error("Delete failed"); }
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/me/avatar", fd);
      loadAvatar();
    } catch (err) {
      // toast handled inline
    } finally {
      setUploadingAvatar(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const totalGiven = donations.reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div>
      <Navbar />
      <section className="pt-28 sm:pt-36 pb-24 bg-muted min-h-screen">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-[#D90429]">My Account</span>
          <div className="flex items-center gap-5 mt-3 flex-wrap">
            <div className="relative group shrink-0" data-testid="account-avatar">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Your avatar" className="w-20 h-20 object-cover border-2 border-[#0A1128]" />
              ) : (
                <div className="w-20 h-20 bg-[#0A1128] flex items-center justify-center"><UserCircle2 className="w-10 h-10 text-white/60" /></div>
              )}
              <button
                type="button"
                data-testid="avatar-upload-button"
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-2 -right-2 bg-[#D90429] text-white p-2 hover:bg-[#b0031f] transition-colors duration-200"
                title="Change photo"
              >
                {uploadingAvatar ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" data-testid="avatar-file-input" onChange={uploadAvatar} className="hidden" />
            </div>
            <div>
              <h1 className="font-display text-5xl sm:text-6xl uppercase leading-[0.95]">
                Welcome, {user?.name?.split(" ")[0] || "Patriot"}
              </h1>
              <p className="text-slate-600 text-lg mt-2">{user?.email}</p>
            </div>
          </div>

          {/* My Chapter (chapter leaders only) */}
          <MyChapter />

          {/* Application status */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-12 bg-white border border-slate-200 p-6 sm:p-8" data-testid="account-applications">
            <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-[#0A1128]">
              <ClipboardList className="w-6 h-6 text-[#D90429]" />
              <h2 className="font-display text-2xl uppercase">Membership Application</h2>
            </div>
            {loading ? (
              <p className="text-slate-400 py-6">Loading…</p>
            ) : apps.length === 0 ? (
              <div className="py-6 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-6">You haven't submitted a membership application yet.</p>
                <Link to="/join" data-testid="account-apply-button" className="inline-flex items-center gap-2 bg-[#D90429] text-white px-8 py-3 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200">
                  Join Our Newsletter <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {apps.map((a) => (
                  <div key={a.id} data-testid="account-application-row" className="flex items-center justify-between flex-wrap gap-3 border border-slate-100 p-4">
                    <div>
                      <div className="font-semibold">{a.state_city} · {a.occupation}</div>
                      <div className="text-sm text-slate-500">Submitted {new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Donation history */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-white border border-slate-200 p-6 sm:p-8" data-testid="account-donations">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6 pb-3 border-b-2 border-[#0A1128]">
              <div className="flex items-center gap-3">
                <HeartHandshake className="w-6 h-6 text-[#D90429]" />
                <h2 className="font-display text-2xl uppercase">Donation History</h2>
              </div>
              {donations.length > 0 && <span className="font-display text-2xl text-[#D90429]">${totalGiven.toLocaleString()} given</span>}
            </div>
            {loading ? (
              <p className="text-slate-400 py-6">Loading…</p>
            ) : donations.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-slate-500 mb-6">No donations yet. Every contribution fuels the mission.</p>
                <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="account-donate-button" className="inline-flex items-center gap-2 bg-[#D90429] text-white px-8 py-3 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200">
                  Donate <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ) : (
              <div className="space-y-3">
                {donations.map((d) => (
                  <div key={d.session_id} data-testid="account-donation-row" className="flex items-center justify-between border border-slate-100 p-4">
                    <span className="text-slate-500 text-sm">{new Date(d.created_at).toLocaleDateString()}</span>
                    <div className="flex items-center gap-3">
                      {d.frequency === "monthly" && <span className="px-2 py-0.5 text-xs uppercase tracking-wider font-semibold bg-[#0A1128] text-white">Monthly</span>}
                      <span className="font-display text-xl text-[#D90429]">${d.amount}{d.frequency === "monthly" ? "/mo" : ""}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-400 mt-4">Donations are matched to your account by email address.</p>
          </motion.div>

          {/* My Events */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-white border border-slate-200 p-6 sm:p-8" data-testid="account-events">
            <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-[#0A1128]">
              <CalendarCheck className="w-6 h-6 text-[#D90429]" />
              <h2 className="font-display text-2xl uppercase">My Events</h2>
            </div>
            {myEvents.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-slate-500 mb-6">You haven't RSVP'd to any events yet.</p>
                <Link to="/events" data-testid="account-browse-events" className="inline-flex items-center gap-2 bg-[#D90429] text-white px-8 py-3 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200">
                  Browse Events <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {myEvents.map((e) => (
                  <div key={e.id} data-testid="account-event-row" className="flex items-center gap-4 border border-slate-100 p-4">
                    <div className="bg-[#0A1128] text-white text-center py-2 w-[60px] shrink-0">
                      <div className="text-[10px] uppercase tracking-widest text-[#F4A261]">{e.month}</div>
                      <div className="font-display text-2xl leading-none">{e.day}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold truncate">{e.title}</h4>
                      <p className="text-sm text-slate-500 flex items-center gap-1 truncate"><MapPin className="w-3.5 h-3.5 text-[#D90429]" /> {e.location} · {e.time}</p>
                    </div>
                    <button data-testid={`cancel-rsvp-${e.id}`} onClick={() => cancelRsvp(e.id)} className="flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70 shrink-0">
                      <X className="w-4 h-4" /> Cancel
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Documents */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-white border border-slate-200 p-6 sm:p-8" data-testid="account-documents">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6 pb-3 border-b-2 border-[#0A1128]">
              <div className="flex items-center gap-3">
                <FolderOpen className="w-6 h-6 text-[#D90429]" />
                <h2 className="font-display text-2xl uppercase">My Documents</h2>
              </div>
              <label data-testid="document-upload-label" className={`inline-flex items-center gap-2 bg-[#D90429] text-white px-5 py-2.5 uppercase tracking-[0.15em] text-xs font-bold cursor-pointer hover:bg-[#b0031f] transition-colors duration-200 ${uploadingDoc ? "opacity-60 pointer-events-none" : ""}`}>
                {uploadingDoc ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload</>}
                <input ref={docRef} type="file" accept="image/*,application/pdf" data-testid="document-upload-input" onChange={uploadDoc} className="hidden" />
              </label>
            </div>
            <p className="text-sm text-slate-500 mb-5">Store your certifications, training records, and ID documents securely (images or PDF, up to 15MB).</p>
            {docs.length === 0 ? (
              <p className="text-slate-400 py-6 text-center">No documents uploaded yet.</p>
            ) : (
              <div className="space-y-3">
                {docs.map((d) => (
                  <div key={d.id} data-testid="document-row" className="flex items-center justify-between border border-slate-100 p-4 gap-4">
                    <span className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-[#D90429] shrink-0" />
                      <span className="truncate">{d.original_filename}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <button data-testid={`document-download-${d.id}`} onClick={() => downloadDoc(d.id)} className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-[#0A1128] px-3 py-2 text-xs font-semibold uppercase transition-colors duration-200"><Download className="w-3.5 h-3.5" /> Open</button>
                      <button data-testid={`document-delete-${d.id}`} onClick={() => deleteDoc(d.id)} className="bg-slate-100 hover:bg-[#D90429] hover:text-white text-[#D90429] p-2 transition-colors duration-200"><Trash2 className="w-3.5 h-3.5" /></button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
